/** A finished film is usable as a timeline only while its saved cut matches the current storyboard. */
type Asset = { id?: string; path?: string };
type Scene = {
  id?: string;
  position?: number;
  start_ms?: number;
  end_ms?: number;
  image_asset_id?: string | null;
  motion_asset_id?: string | null;
  lyric_ids?: string[];
  transition?: string;
  updated_at?: string;
};
type Lyric = {
  id?: string;
  position?: number;
  start_ms?: number;
  end_ms?: number;
  display_text?: string;
  updated_at?: string;
};
type Output = {
  id?: string;
  kind?: string;
  mime_type?: string;
  created_at?: string;
  metadata?: { duration_ms?: number; scene_count?: number };
};
type Project = {
  duration_ms?: number;
  scenes?: Scene[];
  segments?: Lyric[];
  assets?: Asset[];
};
type CutScene = {
  id?: string;
  start_ms?: number;
  end_ms?: number;
  image_path?: string | null;
  motion_path?: string | null;
  lyric_segment_ids?: string[];
  transition?: string;
};
type CutLyric = { id?: string; start_ms?: number; end_ms?: number; text?: string };
type Cut = { durationMs?: number; scenes?: CutScene[]; lyrics?: CutLyric[] };

const path = (value: unknown) => String(value ?? "").replaceAll("\\", "/").toLowerCase();
const samePath = (renderPath: unknown, assetPath: unknown) => {
  const actual = path(renderPath);
  const expected = path(assetPath);
  return expected ? actual === expected || actual.endsWith(`/${expected}`) : !actual;
};
const sameIds = (left: unknown, right: unknown) =>
  JSON.stringify(Array.isArray(left) ? left : []) === JSON.stringify(Array.isArray(right) ? right : []);
const renderedTransition = (value: unknown) => ({
  "dip to black": "fade_black",
  "soft light leak": "fade",
  "match dissolve": "dissolve",
}[String(value ?? "crossfade")] ?? String(value ?? "crossfade"));

export function storyboardSignature(project: Project): string {
  return JSON.stringify({
    durationMs: project.duration_ms,
    scenes: (project.scenes ?? []).map(scene => ({
      id: scene.id, position: scene.position, start: scene.start_ms, end: scene.end_ms,
      image: scene.image_asset_id, motion: scene.motion_asset_id,
      lyrics: scene.lyric_ids, transition: scene.transition, updated: scene.updated_at,
    })),
    lyrics: (project.segments ?? []).map(lyric => ({
      id: lyric.id, position: lyric.position, start: lyric.start_ms, end: lyric.end_ms,
      text: lyric.display_text, updated: lyric.updated_at,
    })),
    assets: (project.assets ?? []).map(asset => ({ id: asset.id, path: asset.path })),
  });
}

export function verifiedFinalMatches(project: Project, output: Output, cut: Cut): boolean {
  if (output.kind !== "final" || output.mime_type !== "video/mp4") return false;
  const scenes = [...(project.scenes ?? [])].sort((a, b) => Number(a.position) - Number(b.position));
  const lyrics = [...(project.segments ?? [])].sort((a, b) => Number(a.position) - Number(b.position));
  if (!scenes.length || !Array.isArray(cut.scenes) || !Array.isArray(cut.lyrics)) return false;
  if (scenes.length !== cut.scenes.length || lyrics.length !== cut.lyrics.length) return false;
  if (Number(project.duration_ms) !== Number(cut.durationMs)
    || Number(output.metadata?.duration_ms) !== Number(cut.durationMs)
    || Number(output.metadata?.scene_count) !== scenes.length) return false;
  const renderedAt = Date.parse(String(output.created_at ?? ""));
  if (!Number.isFinite(renderedAt)) return false;
  if ([...scenes, ...lyrics].some(item => {
    const changedAt = Date.parse(String(item.updated_at ?? ""));
    return !Number.isFinite(changedAt) || changedAt > renderedAt;
  })) return false;
  const assets = new Map((project.assets ?? []).map(asset => [String(asset.id), asset.path]));
  if (scenes.some((scene, index) => {
    const saved = cut.scenes![index]!;
    const image = scene.image_asset_id ? assets.get(scene.image_asset_id) : null;
    const motion = scene.motion_asset_id ? assets.get(scene.motion_asset_id) : null;
    return saved.id !== scene.id || Number(saved.start_ms) !== Number(scene.start_ms)
      || Number(saved.end_ms) !== Number(scene.end_ms)
      || !samePath(saved.image_path, image) || !samePath(saved.motion_path, motion)
      || !sameIds(saved.lyric_segment_ids, scene.lyric_ids)
      || String(saved.transition ?? "") !== renderedTransition(scene.transition);
  })) return false;
  return lyrics.every((lyric, index) => {
    const saved = cut.lyrics![index]!;
    return saved.id === lyric.id && Number(saved.start_ms) === Number(lyric.start_ms)
      && Number(saved.end_ms) === Number(lyric.end_ms)
      && String(saved.text ?? "") === String(lyric.display_text ?? "");
  });
}
